import React, { useState } from 'react';
import { compact } from 'lodash';
import { Card } from '../../../components/Card/Card';
import { t } from '../../../locales/translate';
import { Address, Company } from '../../../model/company';
import { useRemoteData } from '../../../model/data';
import { getCountries, Country } from '../../../model/country';
import { Icon } from '../../../components/Icon/Icon';
import { Label } from '../../../components/Form/Label/Label';
import { KeyValueTable } from '../../../components/KeyValueTable/KeyValueTable';
import { CompanyForm } from '../CompaniesModal/CompanyForm';
import { Modal } from '../../../components/Modal/Modal';
import { formatPercentage } from '../../../util/formatters';

interface Props {
  company: Company;
}

function getCompanyCity(address: Address, country?: Country): string {
  const filteredAdress = compact([address.postCode, address.city, country?.name]);

  if (filteredAdress.length > 1) {
    return filteredAdress.join(', ');
  }
  if (filteredAdress.length === 1) {
    return filteredAdress[0];
  }
  return '';
}

export function BillingAddress({ company }: Props): JSX.Element {
  const [country] = useRemoteData(getCountries, (db) => {
    return db.table('countries').where((item) => item.countryCode === (company ? company.address.countryCode : ''));
  });
  const [showCompanyEditModal, setShowCompanyEditModal] = useState(false);
  const emptyAddress = compact(Object.values(company.address)).length === 0;

  return (
    <>
      <Card
        title={t.model.company.billingAddress}
        elevation={1}
        className="company-details__billing-address"
        actions={
          company.$permissions.includes('update') && (
            <span style={{ cursor: 'pointer' }} onClick={() => setShowCompanyEditModal(true)}>
              <Icon name="edit" />
            </span>
          )
        }
      >
        <div>
          {emptyAddress ? (
            <div>{t.model.company.noRegisteredAddress}</div>
          ) : (
            <div>
              <p>{t.model.company.invoices}</p>
              <div>
                <strong>{company.name}</strong>
              </div>
              <div>{company.address.address}</div>
              <div>{getCompanyCity(company.address, country[0])}</div>
            </div>
          )}
          <Label title={t.model.invoice.invoicing} />
          <KeyValueTable
            items={[
              {
                key: t.model.invoice.invoicingCycle,
                value: company.invoicingCycle && t.model.invoice.invoicingCycles[company.invoicingCycle],
              },
              {
                key: t.model.invoice.eventPayoutCycle,
                value: company.eventPayoutCycle && t.model.invoice.invoicingCycles[company.eventPayoutCycle],
              },
              {
                key: t.model.invoice.defaultEventPayoutPercentage,
                value: company.defaultEventPayoutPercentage
                  ? formatPercentage(company.defaultEventPayoutPercentage)
                  : undefined,
              },
              {
                key: t.model.invoice.billingEmails,
                value: company.billingEmails?.join(', '),
              },
            ]}
          />
        </div>
      </Card>
      <Modal open={showCompanyEditModal} title={t.actions.edit(t.model.company._name)} size="small">
        <CompanyForm company={company} onClose={() => setShowCompanyEditModal(false)} />
      </Modal>
    </>
  );
}
